// ==============================
// src/services/auth.js
// Servicio de autenticación para botbase
// ==============================

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8003';

// ============================================================
// MANEJO DE TOKEN EN LOCALSTORAGE
// ============================================================

export function setToken(token) {
  localStorage.setItem('auth_token', token);
}

export function getToken() {
  return localStorage.getItem('auth_token');
}

export function removeToken() {
  localStorage.removeItem('auth_token');
}

export function isAuthenticated() {
  return !!getToken();
}

export function decodeToken(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decodificando token:', error);
    return null;
  }
}

export function getEmpresaIdFromToken() {
  const token = getToken();
  if (!token) return null;
  const decoded = decodeToken(token);
  return decoded?.empresa_id || null;
}

export function getRolFromToken() {
  const token = getToken();
  if (!token) return null;
  const decoded = decodeToken(token);
  return decoded?.rol || null;
}

export function getNombreFromToken() {
  const token = getToken();
  if (!token) return null;
  const decoded = decodeToken(token);
  return decoded?.nombre || decoded?.email || 'Usuario';
}

// ============================================================
// HEADERS PARA PETICIONES
// ============================================================

function getHeaders(extraHeaders = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
    ...extraHeaders
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export function authHeader() {
  const token = getToken();
  const headers = { 'ngrok-skip-browser-warning': 'true' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// ============================================================
// LOGIN (usa OAuth2PasswordRequestForm con x-www-form-urlencoded)
// ============================================================

export async function login(credentials) {
  const formData = new URLSearchParams();
  formData.append('username', credentials.username);
  formData.append('password', credentials.password);

  const response = await fetch(`${API_URL}/api/v1/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'ngrok-skip-browser-warning': 'true',
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Error al iniciar sesión');
  }

  const data = await response.json();
  setToken(data.access_token);
  return data;
}

// ============================================================
// LOGOUT
// ============================================================

export function logout() {
  removeToken();
  window.location.href = '/login';
}

// ============================================================
// REGISTRO (crea usuario + empresa)
// ============================================================

export async function register(registerData) {
  const response = await fetch(`${API_URL}/api/v1/auth/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true',
    },
    body: JSON.stringify(registerData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Error al registrar usuario');
  }

  return response.json();
}

// ============================================================
// VERIFICACIÓN DE EMAIL
// ============================================================

export async function verificarEmail(token) {
  const response = await fetch(`${API_URL}/api/v1/auth/verificar?token=${token}`, {
    method: 'GET',
    headers: {
      'ngrok-skip-browser-warning': 'true',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Error al verificar email');
  }

  return response.json();
}

// ============================================================
// RECUPERACIÓN DE CONTRASEÑA
// ============================================================

export async function solicitarRecuperacion(email) {
  const response = await fetch(`${API_URL}/api/v1/auth/recuperar`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true',
    },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Error al solicitar recuperación');
  }

  return response.json();
}

export async function restablecerPassword(token, nuevaPassword) {
  const response = await fetch(`${API_URL}/api/v1/auth/restablecer`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true',
    },
    body: JSON.stringify({ token, nueva_password: nuevaPassword }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Error al restablecer la contraseña');
  }

  return response.json();
}

// ============================================================
// EMBEDDED SIGNUP - CALLBACK DE META
// ============================================================

export async function metaCallback(code) {
  const response = await fetch(`${API_URL}/api/v1/auth/meta/callback?code=${code}`, {
    method: 'POST',
    headers: authHeader(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Error al conectar con WhatsApp');
  }

  return response.json();
}